// Endpoint pricing display helper — Ring 3 PR B.
//
// Deliberately separate from utils/formatPrice.ts, which is a
// KLV/USD/BTC display-unit abstraction for whole-KLV amounts. This
// file works with SMALLEST-UNIT BigInt strings across many chains
// (the shape agent_endpoint.price_{min,max}_smallest hands the UI)
// and derives the four display cases from the (min, max, asset)
// triple.
//
// Display rules (see ADR-013 discussion + PR #573 handoff):
//   min = 0  AND  max = 0   → "Free"
//   min == max              → "0.5 KLV"     (fixed)
//   min = 0  AND  max > 0   → "Up to 2 KLV" (capped, pay-what-you-can)
//   0 < min < max           → "0.1 – 2 KLV" (range)
//
// Unknown asset falls back to the raw smallest-unit numbers so we
// never lie about precision.

/**
 * Payment-asset allowlist that the endpoint pricing UI picks from.
 * Superset of AgentChain (the chain an agent operates on) because
 * some assets aren't chains — USDC is a stablecoin that settles on
 * multiple EVM + Solana chains.
 *
 * **Sync policy (#653):** this list mirrors PAYMENT_ASSETS in
 * server/src/services/AgentService.ts. The Zod schema on the create
 * endpoint uses the server list as a strict enum and rejects any
 * value that isn't in it — so if you add an asset here without
 * adding it there, agents will see a "Invalid enum value" 400 the
 * moment they try to save. Update BOTH sides in the same commit.
 *
 * USDC first — x402-canonical stablecoin + it's the composer/Zod
 * default.
 */
// BTC dropped 2026-09-13 — no live x402 facilitator supports native
// BTC settlement. Re-add if / when one lands; #663 tracks the audit.
export const PAYMENT_ASSETS = ['USDC', 'KLV', 'ETH', 'SOL', 'BASE'] as const;

/**
 * Which chains each asset can settle on. Mirror of
 * PAYMENT_ASSET_ALLOWED_CHAINS in server/src/services/AgentService.ts —
 * see the sync-policy comment there. Powers the accepted-lanes matrix
 * on /agents/mine/edit (which (asset, chain) cells are possible).
 */
export const PAYMENT_ASSET_ALLOWED_CHAINS: Record<string, readonly string[]> = {
  USDC: ['klv', 'eth', 'base', 'sol'],
  KLV:  ['klv'],
  ETH:  ['eth'],
  SOL:  ['sol'],
  BASE: ['base'],
};

/** All chains any PAYMENT_ASSET can settle on, deduped + sorted so
 *  the matrix headers have a stable column order. */
export const ALL_PAYMENT_CHAINS: readonly string[] = ['klv', 'eth', 'base', 'sol'];

export type PaymentAsset = (typeof PAYMENT_ASSETS)[number];

const DECIMALS: Record<string, number> = {
  KLV: 6,
  KLYX: 8,
  ETH: 18,
  BASE: 18,
  SOL: 9,
  // USDC — 6 decimals across every chain it lives on (Ethereum,
  // Base, Solana). Same value as USDT / most stablecoins.
  USDC: 6,
};

/**
 * Decimal precision for a given asset ticker. Undefined for unknown
 * assets — callers should either reject or fall back to raw
 * smallest-unit display.
 */
export function decimalsForAsset(asset: string): number | undefined {
  return DECIMALS[asset.toUpperCase()];
}

/**
 * Parse a user-typed whole-unit amount ("0.5", "1", "2.75") into
 * a smallest-unit BigInt string. Returns null for unparseable
 * input OR for an unknown asset (caller decides how to warn the
 * user; UI should disable submit + surface an error).
 *
 * Preserves precision through BigInt — a whole-unit "0.5" for KLV
 * becomes "500000", not "499999.9…".
 */
export function parseAmountToSmallest(
  whole: string,
  asset: string,
): string | null {
  const trimmed = whole.trim();
  if (trimmed === '') return null;
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const decimals = decimalsForAsset(asset);
  if (decimals === undefined) return null;

  const [wholePart, fracPartRaw = ''] = trimmed.split('.');
  const fracPart = fracPartRaw.slice(0, decimals).padEnd(decimals, '0');
  // Whole + fractional as one big-integer string; strip leading
  // zeros so '0500000' → '500000' but '0' stays '0'.
  const combined = `${wholePart}${fracPart}`.replace(/^0+(\d)/, '$1');
  return combined;
}

/**
 * Inverse of parseAmountToSmallest — smallest-unit → whole-unit
 * string. Trims trailing zeros in the fractional part. Returns raw
 * smallest string if the asset is unknown.
 */
export function smallestToWhole(smallest: string, asset: string): string {
  const decimals = decimalsForAsset(asset);
  if (decimals === undefined) return smallest;
  return toDecimalString(smallest, decimals);
}

/**
 * BigInt-safe format of a smallest-unit decimal string. Preserves
 * exact precision (no Number ever touches the value). Trims trailing
 * zeroes in the fractional part.
 *
 *   toDecimalString('500000', 6) → '0.5'
 *   toDecimalString('2000000', 6) → '2'
 *   toDecimalString('100', 6)     → '0.0001'
 */
function toDecimalString(smallest: string, decimals: number): string {
  const n = BigInt(smallest);
  if (decimals === 0) return n.toString();
  const base = 10n ** BigInt(decimals);
  const whole = n / base;
  const frac = n % base;
  if (frac === 0n) return whole.toString();
  const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '');
  return `${whole}.${fracStr}`;
}

export interface FormatEndpointPriceInput {
  priceMinSmallest: string;
  priceMaxSmallest: string;
  priceAsset: string;
}

/**
 * Render the display string for an endpoint's price range. Handles
 * the four cases above. Never returns a per-call suffix — callers
 * can append "per call" or similar for context if they want.
 */
export function formatEndpointPrice(input: FormatEndpointPriceInput): string {
  const asset = input.priceAsset.toUpperCase();
  const decimals = DECIMALS[asset];

  const isZero = (s: string): boolean => {
    try {
      return BigInt(s) === 0n;
    } catch {
      return false;
    }
  };

  if (isZero(input.priceMinSmallest) && isZero(input.priceMaxSmallest)) {
    return 'Free';
  }

  // Fallback if we don't know the decimals — leave the raw numbers
  // in so the user isn't misled by a possibly-off display.
  if (decimals === undefined) {
    if (input.priceMinSmallest === input.priceMaxSmallest) {
      return `${input.priceMinSmallest} ${asset}`;
    }
    if (isZero(input.priceMinSmallest)) {
      return `Up to ${input.priceMaxSmallest} ${asset}`;
    }
    return `${input.priceMinSmallest} – ${input.priceMaxSmallest} ${asset}`;
  }

  const min = toDecimalString(input.priceMinSmallest, decimals);
  const max = toDecimalString(input.priceMaxSmallest, decimals);

  if (input.priceMinSmallest === input.priceMaxSmallest) {
    return `${min} ${asset}`;
  }
  if (isZero(input.priceMinSmallest)) {
    return `Up to ${max} ${asset}`;
  }
  return `${min} – ${max} ${asset}`;
}
